import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SignedImage } from '../../hooks/useSignedUrl';
import Logo from '../../assets/lumin-logo.png';
import { NavItem } from '../../constants/navigation';
import NotificationBell from '../notifications/NotificationBell';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface AdminShellProps {
  title: string;
  description?: string;
  nav: NavItem[];
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const NavDropdown: React.FC<{ item: NavItem; location: ReturnType<typeof useLocation> }> = ({ item, location }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const Icon = item.icon;

  // Check if any child is active
  const isChildActive = item.children?.some(
    (child) => child.exact ? location.pathname === child.to : location.pathname.startsWith(child.to)
  );

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 ${
          isChildActive ? 'text-white border-white' : 'text-white/70 border-transparent hover:text-white'
        }`}
      >
        {Icon && <Icon className="h-4 w-4" />}
        {item.label}
        <ChevronDownIcon className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed bg-[#014f70] rounded-b-lg shadow-lg min-w-[180px] z-50"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          {item.children?.map((child) => {
            const ChildIcon = child.icon;
            const isActive = child.exact ? location.pathname === child.to : location.pathname.startsWith(child.to);
            return (
              <Link
                key={child.to}
                to={child.to}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium ${
                  isActive ? 'text-white bg-white/10' : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {ChildIcon && <ChildIcon className="h-4 w-4" />}
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
};

const AdminShell: React.FC<AdminShellProps> = ({ title, description, nav, actions, children }) => {
  const { loanOfficer, signOut } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#f5f7fb] text-slate-900">
      <header className="bg-lumin-primary text-white shadow-md">
        <div className="max-w-[1800px] mx-auto px-6 py-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <img src={Logo} alt="Lumin" className="h-9" />
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/80">Admin Portal</p>
              <h1 className="text-2xl font-semibold">{title}</h1>
              {description && <p className="text-sm text-white/80">{description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {actions}
            <NotificationBell />
            {loanOfficer && (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-semibold text-white">
                    {loanOfficer.first_name} {loanOfficer.last_name}
                  </p>
                  <p className="text-xs text-white/70">Admin</p>
                </div>
                <SignedImage
                  src={loanOfficer.headshot}
                  alt={`${loanOfficer.first_name} ${loanOfficer.last_name}`}
                  className="h-10 w-10 rounded-full object-cover border-2 border-white/20"
                  fallback={
                    <div className="h-10 w-10 rounded-full bg-lumin-accent flex items-center justify-center text-white font-bold text-sm">
                      {loanOfficer.first_name?.charAt(0)}
                      {loanOfficer.last_name?.charAt(0)}
                    </div>
                  }
                />
                <button
                  onClick={signOut}
                  className="ml-2 px-3 py-1.5 text-xs font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
        <nav className="bg-[#014f70]">
          <div className="max-w-[1800px] mx-auto px-6 flex items-center overflow-x-auto">
            {nav.map((item) => {
              // Render dropdown for items with children
              if (item.children && item.children.length > 0) {
                return <NavDropdown key={item.to} item={item} location={location} />;
              }

              const isActive = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 ${
                    isActive ? 'text-white border-white' : 'text-white/70 border-transparent hover:text-white'
                  }`}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-10 space-y-8">
        {children}
      </main>
    </div>
  );
};

export default AdminShell;
